import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, DollarSign, TrendingUp, Download, RefreshCw, ExternalLink, Zap, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import api from '../../services/api';

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface MrrPorPlano {
  plano: string;
  quantidade: number;
  mrr: number;
}

interface Kpis {
  mrr: number;
  receita_paga: number;
  taxa_arrecadacao: number;
  creches_ativas: number;
  total_assinaturas: number;
  cobrancas_pendentes: number;
  cobrancas_atrasadas: number;
  cancelamentos_mes: number;
  churn_rate: number;
  ltv: number;
  ticket_medio: number;
  mrr_por_plano: MrrPorPlano[];
}

interface Cobranca {
  id: number;
  id_creche: number;
  id_assinatura: number;
  valor: number;
  data_vencimento: string;
  data_pagamento: string | null;
  status: 'pendente' | 'pago' | 'atrasado' | 'estornado' | 'cancelado';
  gateway_ref: string | null;
  metodo_pagamento: string | null;
  checkout_url: string | null;
  pix_copia_cola: string | null;
  observacoes: string | null;
  criado_em: string;
  // join virtual
  creche_nome?: string;
}

interface Assinatura {
  id: number;
  id_creche: number;
  plano: string;
  metodo_pagamento: string;
  status_assinatura: string;
  valor_mensal: number;
  cobranca_ativa: boolean;
}

// ─── Utilitários ──────────────────────────────────────────────────────────────

function fmtMoeda(v: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);
}

function fmtData(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('pt-BR');
}

const STATUS_CONFIG: Record<string, { cor: string; label: string; icone: React.ReactNode }> = {
  pago:      { cor: 'bg-green-100 text-green-700',  label: 'Pago',      icone: <CheckCircle size={12} /> },
  pendente:  { cor: 'bg-yellow-100 text-yellow-700', label: 'Pendente',  icone: <Clock size={12} /> },
  atrasado:  { cor: 'bg-red-100 text-red-700',      label: 'Atrasado',  icone: <AlertCircle size={12} /> },
  estornado: { cor: 'bg-purple-100 text-purple-700', label: 'Estornado', icone: <RefreshCw size={12} /> },
  cancelado: { cor: 'bg-gray-100 text-gray-600',    label: 'Cancelado', icone: <AlertCircle size={12} /> },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.pendente;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.cor}`}>
      {cfg.icone} {cfg.label}
    </span>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function FaturamentoPage() {
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [cobrancas, setCobrancas] = useState<Cobranca[]>([]);
  const [assinaturas, setAssinaturas] = useState<Assinatura[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [gerando, setGerando] = useState<number | null>(null);
  const [filtroStatus, setFiltroStatus] = useState<string>('todos');
  const [pixCopiado, setPixCopiado] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const [resKpis, resCobrancas, resAssinaturas] = await Promise.all([
        api.get<Kpis>('/saas/faturamento/kpis'),
        api.get<Cobranca[]>('/saas/faturas'),
        api.get<Assinatura[]>('/saas/assinaturas'),
      ]);
      setKpis(resKpis.data);
      setCobrancas(resCobrancas.data ?? []);
      setAssinaturas(resAssinaturas.data ?? []);
    } catch (e: any) {
      setErro(e?.response?.data?.message ?? 'Erro ao carregar dados de faturamento');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  async function handleGerarCobranca(idCreche: number) {
    setGerando(idCreche);
    try {
      await api.post(`/saas/tenants/${idCreche}/cobrar`, { metodo: 'PIX' });
      await carregar();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? 'Erro ao gerar cobrança');
    } finally {
      setGerando(null);
    }
  }

  async function handleCopiarPix(cobranca: Cobranca) {
    const texto = cobranca.pix_copia_cola ?? cobranca.checkout_url ?? '';
    if (!texto) return;
    await navigator.clipboard.writeText(texto);
    setPixCopiado(cobranca.id);
    setTimeout(() => setPixCopiado(null), 2500);
  }

  // Enriquecer cobranças com nome da creche (via assinaturas)
  const assinaturaMap = Object.fromEntries(assinaturas.map((a) => [a.id, a]));
  const cobrancasEnriquecidas = cobrancas.map((c) => ({
    ...c,
    assinatura: assinaturaMap[c.id_assinatura],
  }));

  const cobrancasFiltradas = cobrancasEnriquecidas.filter(
    (c) => filtroStatus === 'todos' || c.status === filtroStatus,
  );

  const taxaArrecadacao = kpis?.taxa_arrecadacao ?? 0;

  if (carregando) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mb-4" />
          <p className="text-gray-600">Carregando dados de faturamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* ── Cabeçalho ── */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-700 rounded-2xl p-8 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold mb-2">💰 Faturamento SaaS</h1>
            <p className="text-green-100">Receitas, cobranças e integração com Asaas</p>
          </div>
          <button
            onClick={carregar}
            className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl transition text-white font-semibold"
          >
            <RefreshCw size={16} /> Atualizar
          </button>
        </div>
      </div>

      {/* ── Erro ── */}
      {erro && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 text-red-700">
          <AlertCircle size={20} /> <span>{erro}</span>
        </div>
      )}

      {/* ── KPIs ── */}
      {/* Linha 1: MRR + Arrecadado + Pendentes + Atrasadas */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-green-200">
          <div className="flex items-start justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">MRR</p>
            <div className="p-1.5 bg-green-100 rounded-lg"><DollarSign className="text-green-600" size={16} /></div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{fmtMoeda(kpis?.mrr ?? 0)}</h3>
          <p className="text-xs text-green-600 font-semibold mt-1">Receita Recorrente Mensal</p>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm border border-blue-200">
          <div className="flex items-start justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Arrecadado</p>
            <div className="p-1.5 bg-blue-100 rounded-lg"><TrendingUp className="text-blue-600" size={16} /></div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{fmtMoeda(kpis?.receita_paga ?? 0)}</h3>
          <p className="text-xs text-blue-600 font-semibold mt-1">Taxa: {taxaArrecadacao}%</p>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm border border-yellow-200">
          <div className="flex items-start justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Pendentes</p>
            <div className="p-1.5 bg-yellow-100 rounded-lg"><Clock className="text-yellow-600" size={16} /></div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{kpis?.cobrancas_pendentes ?? 0}</h3>
          <p className="text-xs text-yellow-600 font-semibold mt-1">Em aberto</p>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm border border-red-200">
          <div className="flex items-start justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Atrasadas</p>
            <div className="p-1.5 bg-red-100 rounded-lg"><AlertCircle className="text-red-500" size={16} /></div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{kpis?.cobrancas_atrasadas ?? 0}</h3>
          <p className="text-xs text-red-500 font-semibold mt-1">Inadimplentes</p>
        </div>
      </div>

      {/* Linha 2: Tenants ativos + Churn + LTV + Ticket médio */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-purple-200">
          <div className="flex items-start justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tenants Ativos</p>
            <div className="p-1.5 bg-purple-100 rounded-lg"><Zap className="text-purple-600" size={16} /></div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{kpis?.creches_ativas ?? 0}</h3>
          <p className="text-xs text-purple-600 font-semibold mt-1">Com assinatura ativa</p>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm border border-orange-200">
          <div className="flex items-start justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Churn</p>
            <div className="p-1.5 bg-orange-100 rounded-lg"><TrendingUp className="text-orange-500" size={16} /></div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{kpis?.churn_rate ?? 0}%</h3>
          <p className="text-xs text-orange-500 font-semibold mt-1">{kpis?.cancelamentos_mes ?? 0} cancelamento(s)/mês</p>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm border border-teal-200">
          <div className="flex items-start justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">LTV Estimado</p>
            <div className="p-1.5 bg-teal-100 rounded-lg"><DollarSign className="text-teal-600" size={16} /></div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{fmtMoeda(kpis?.ltv ?? 0)}</h3>
          <p className="text-xs text-teal-600 font-semibold mt-1">Lifetime Value por tenant</p>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm border border-indigo-200">
          <div className="flex items-start justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Ticket Médio</p>
            <div className="p-1.5 bg-indigo-100 rounded-lg"><DollarSign className="text-indigo-600" size={16} /></div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{fmtMoeda(kpis?.ticket_medio ?? 0)}</h3>
          <p className="text-xs text-indigo-600 font-semibold mt-1">Por assinatura ativa</p>
        </div>
      </div>

      {/* MRR por Plano */}
      {(kpis?.mrr_por_plano?.length ?? 0) > 0 && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-base font-bold text-gray-800 mb-4">📊 MRR por Plano</h2>
          <div className="flex flex-wrap gap-4">
            {kpis!.mrr_por_plano.map((p) => (
              <div key={p.plano} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
                <div>
                  <p className="text-xs text-gray-500 font-semibold uppercase">{p.plano}</p>
                  <p className="text-lg font-bold text-gray-900">{fmtMoeda(p.mrr)}</p>
                  <p className="text-xs text-gray-400">{p.quantidade} tenant(s)</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Assinaturas ativas — gerar cobrança ── */}
      {assinaturas.filter((a) => a.cobranca_ativa).length > 0 && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4">⚡ Gerar Cobrança Mensal</h2>
          <p className="text-sm text-gray-500 mb-4">
            Emite uma cobrança via Pix para o tenant. O link de pagamento ficará disponível na tabela abaixo.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {assinaturas
              .filter((a) => a.cobranca_ativa)
              .map((a) => (
                <div
                  key={a.id}
                  className="flex items-center justify-between border border-gray-100 rounded-xl p-3 bg-gray-50"
                >
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">Creche #{a.id_creche}</p>
                    <p className="text-xs text-gray-500">
                      {a.plano} — {fmtMoeda(a.valor_mensal)}
                    </p>
                  </div>
                  <button
                    onClick={() => handleGerarCobranca(a.id_creche)}
                    disabled={gerando === a.id_creche}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition"
                  >
                    {gerando === a.id_creche ? (
                      <><RefreshCw size={12} className="animate-spin" /> Gerando...</>
                    ) : (
                      <><Zap size={12} /> Cobrar Pix</>
                    )}
                  </button>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* ── Histórico de Cobranças ── */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <h2 className="text-xl font-bold text-gray-900">📋 Histórico de Cobranças</h2>
          <div className="flex items-center gap-3">
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            >
              <option value="todos">Todos os status</option>
              <option value="pendente">Pendente</option>
              <option value="pago">Pago</option>
              <option value="atrasado">Atrasado</option>
              <option value="estornado">Estornado</option>
              <option value="cancelado">Cancelado</option>
            </select>
            <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg transition">
              <Download size={16} /> Exportar
            </button>
          </div>
        </div>

        {cobrancasFiltradas.length === 0 ? (
          <div className="py-16 text-center text-gray-400">
            <DollarSign size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-lg font-semibold">Nenhuma cobrança encontrada</p>
            <p className="text-sm">Use o botão acima para gerar a primeira cobrança.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-3 font-semibold text-gray-600">#</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-600">Creche</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-600">Valor</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-600">Vencimento</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-600">Pagamento</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-600">Método</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-600">Status</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-600">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {cobrancasFiltradas.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50 transition">
                    <td className="py-3 px-3 text-gray-400 font-mono text-xs">#{c.id}</td>
                    <td className="py-3 px-3">
                      <div>
                        <p className="font-semibold text-gray-800">Creche #{c.id_creche}</p>
                        {c.observacoes && (
                          <p className="text-xs text-gray-400 truncate max-w-[140px]">{c.observacoes}</p>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-3 font-bold text-gray-900">{fmtMoeda(c.valor)}</td>
                    <td className="py-3 px-3 text-gray-600">{fmtData(c.data_vencimento)}</td>
                    <td className="py-3 px-3 text-gray-600">{fmtData(c.data_pagamento)}</td>
                    <td className="py-3 px-3">
                      <span className="uppercase text-xs font-semibold text-gray-500">
                        {c.metodo_pagamento ?? '—'}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      <StatusBadge status={c.status} />
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        {c.checkout_url && (
                          <a
                            href={c.checkout_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Abrir fatura"
                            className="p-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition"
                          >
                            <ExternalLink size={14} />
                          </a>
                        )}
                        {(c.pix_copia_cola || c.checkout_url) && c.status === 'pendente' && (
                          <button
                            onClick={() => handleCopiarPix(c)}
                            title="Copiar Pix Copia e Cola"
                            className="p-1.5 bg-green-50 hover:bg-green-100 text-green-600 rounded-lg transition text-xs font-semibold"
                          >
                            {pixCopiado === c.id ? '✓ Copiado' : '📋 Pix'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-gray-400 mt-4 text-right">
          {cobrancasFiltradas.length} registro(s) exibido(s)
        </p>
      </div>

    </div>
  );
}
